// ManagerUserModal.tsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import { Backdrop, CircularProgress } from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { deleteUserFromList, getAllUsersAndFoldersPermissions } from "../api"; // Ajuste o caminho conforme necessário
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import EntityFilter from "./EntityFilter";

interface Permission {
  folderPath: string;
  accessPermission: boolean;
}

interface UserFolderData {
  userId: number; // Novo campo
  login: string;
  permissions: Permission[];
  fullname: string;
}

interface ManagerUserModalProps {
  open: boolean;
  onClose: () => void;
  userFoldersData: UserFolderData[];
  setUserFoldersData: React.Dispatch<React.SetStateAction<UserFolderData[]>>;
  refreshAdminUsers: () => void; // Nova prop para atualizar a lista do Admin
}

interface RowData {
  userId: number;
  login: string;
  name: string; // Novo campo
  entidades: string[];
  isCurrentUser: boolean;
}

interface DeleteUserFromListRequest {
  AdminUsername: string;
  UsersToDelete: {
    Username: string;
    UserId: number;
  }[];
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  }
);

const ManagerUserModal: React.FC<ManagerUserModalProps> = ({
  open,
  onClose,
  userFoldersData,
  setUserFoldersData,
  refreshAdminUsers, // Inclua aqui
}) => {
  const theme = useTheme(); // Obtém o tema
  const [filter, setFilter] = useState<{
    type: "login" | "name";
    value: string;
  }>({
    type: "login", // Tipo de filtro: login ou name
    value: "", // Valor do filtro
  });
  const [entidadeFilter, setEntidadeFilter] = useState<string>("");
  const storedUser = sessionStorage.getItem("user");
  const currentUser = storedUser ? JSON.parse(storedUser).login : "";

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [usersToDelete, setUsersToDelete] = useState<RowData[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [shouldShowContent, setShouldShowContent] = useState(false);
  const [loading, setLoading] = useState(false); // se ainda não tiver
  const [deleting, setDeleting] = useState(false); // novo loading exclusivo para exclusão


  const rootFolder = window._env_?.REACT_APP_ROOT_FOLDER || "";

  const normalizePath = (path: string) =>
    path.replace(/\\/g, "/").replace(/\/+/g, "/");

  // Normaliza o caminho base para garantir a consistência
  const normalizedRootFolder = normalizePath(rootFolder).endsWith("/")
    ? normalizePath(rootFolder)
    : normalizePath(rootFolder) + "/";

    const extractEntity = (folderPath: string) => {
      const normalizePath = (path: string) =>
        path.replace(/\\/g, "/").replace(/\/+/g, "/");
    
      const normalizedPath = normalizePath(folderPath).toLowerCase();
      const rawRoot = rootFolder;
      const normalizedRoot = normalizePath(rawRoot).replace(/\/+$/, "").toLowerCase();
    
      // Se não começa com rootFolder, ignora
      if (!normalizedPath.startsWith(normalizedRoot)) {
        console.warn("🛑 Caminho fora do root:", {
          root: normalizedRoot,
          path: normalizedPath,
        });
        return null;
      }
    
      const relative = normalizedPath.slice(normalizedRoot.length);
      const entidade = relative.startsWith("/") ? relative.slice(1).split("/")[0] : relative.split("/")[0];
    
      // Se não tiver nada além do root, retorna o próprio caminho root
      return entidade && entidade.trim() !== "" ? entidade : rawRoot;
    };
    
    

  const rows: RowData[] = userFoldersData.map((user) => {
    const entidades = Array.from(
      new Set(
        user.permissions
          .map((permission) => extractEntity(permission.folderPath)) // Extrai entidades
          .filter((entity): entity is string => !!entity) // Remove valores nulos e garante que o tipo seja string
      )
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })); // Ordena alfabeticamente (considerando números também)

    const isCurrentUser = user.login === currentUser;
    return {
      userId: user.userId,
      login: user.login,
      name: user.fullname || "N/A",
      entidades,
      isCurrentUser,
    };
  });

  // Ordenação com base no tipo de filtro e mantendo o usuário atual como o primeiro
  const sortedRows: RowData[] = rows.sort((a, b) => {
    // Sempre mantenha o usuário atual como o primeiro
    if (a.isCurrentUser) return -1;
    if (b.isCurrentUser) return 1;

    if (filter.type === "login") {
      // Ordenação por Login considerando valores numéricos
      const loginA = parseInt(a.login, 10) || 0; // Converte para número (0 se inválido)
      const loginB = parseInt(b.login, 10) || 0;
      return loginA - loginB; // Ordenação numérica
    } else {
      // Ordenação por Nome (alfabética e crescente)
      return a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    }
  });

  useEffect(() => {
    // Limpa as seleções quando o filtro for alterado
    setSelectedRows([]); // Reseta as seleções de lixeiras
  }, [filter.value, entidadeFilter]); // Monitorando as mudanças nos filtros

  useEffect(() => {
    if (!open) return;
  
    const storedUser = sessionStorage.getItem("user");
    if (!storedUser) return;
  
    setLoading(true);
    setShouldShowContent(false);
  
    const { login } = JSON.parse(storedUser);
  
    getAllUsersAndFoldersPermissions(login)
      .then((data) => {
        const mappedData = data.map((item) => ({
          ...item,
          fullname: item.fullName || "N/A",
        }));
        setUserFoldersData(mappedData);
        setShouldShowContent(true);
      })
      .catch((err) => {
        toast.error("Erro ao buscar dados de usuários.");
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]); // <- esse open precisa estar correto
  

  // Aplicação de filtros na lista ordenada
  const filteredRows = sortedRows.filter((row) => {
    const matchesType =
      filter.type === "login"
        ? row.login.toLowerCase().includes(filter.value.toLowerCase())
        : row.name.toLowerCase().includes(filter.value.toLowerCase());

    const matchesEntidade =
      entidadeFilter === "" ||
      row.entidades.some((entidade) =>
        entidade.toLowerCase().includes(entidadeFilter.toLowerCase())
      );

    return matchesType && matchesEntidade; // Combina os dois filtros
  });

  const toggleRow = (index: number) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const toggleAllRows = () => {
    const selectableIndices = filteredRows
      .map((row, idx) => ({ row, idx }))
      .filter(({ row }) => !row.isCurrentUser) // Filtra os usuários que podem ser selecionados
      .map(({ idx }) => idx);

    if (selectedRows.length === selectableIndices.length) {
      setSelectedRows([]); // Deseleciona todos
    } else {
      setSelectedRows(selectableIndices); // Seleciona todos os disponíveis
    }
  };

  const areAllRowsSelected =
    filteredRows.filter((row) => !row.isCurrentUser).length > 0 &&
    selectedRows.length ===
      filteredRows.filter((row) => !row.isCurrentUser).length;

  // Função para baixar CSV com preservação de zeros iniciais no login
  const handleDownloadCSV = () => {
    // Definir os cabeçalhos do CSV
    const headers = ["Login", "Nome", "Entidades"]; // Adicionando 'Nome' entre 'Login' e 'Entidades'

    // Mapear os dados filtrados para o formato adequado
    const csvRows = filteredRows.map((row) => [
      `"${row.login}"`, // Coloca login entre aspas duplas
      `"${row.name}"`, // Coloca nome entre aspas duplas
      `"${row.entidades.join(", ")}"`, // Coloca entidades entre aspas duplas
    ]);

    // Construir o conteúdo do CSV
    let csvContent = headers.join(",") + "\n";
    csvRows.forEach((rowArray) => {
      const row = rowArray.join(",");
      csvContent += row + "\n";
    });

    // Criar um Blob a partir do conteúdo CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    // Criar um link temporário para download
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "usuarios_entidades.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Função para abrir o modal de confirmação
  const handleSave = () => {
    const selectedUsers = selectedRows.map(
      (rowIndex) => filteredRows[rowIndex]
    );
    setUsersToDelete(selectedUsers);
    setIsConfirmOpen(true);
  };

  const refreshUserList = async () => {
    try {
      const storedUser = sessionStorage.getItem("user");
      if (!storedUser) {
        throw new Error("Usuário não encontrado no sessionStorage");
      }

      const { login } = JSON.parse(storedUser);
      const updatedData = await getAllUsersAndFoldersPermissions(login);

      // Mapear os dados para garantir a estrutura correta
      const mappedData = updatedData.map((item) => ({
        ...item,
        fullname: item.fullName || "N/A", // Garantir a propriedade fullname
      }));

      setUserFoldersData(mappedData); // Atualiza a lista usando os dados mapeados
    } catch (error) {
      toast.error("Erro ao atualizar a lista de usuários.");
      console.error("Erro ao atualizar a lista de usuários:", error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true); // <-- aqui
  
      const deleteUserFromListRequest: DeleteUserFromListRequest = {
        AdminUsername: currentUser,
        UsersToDelete: usersToDelete.map((user) => ({
          Username: user.login,
          UserId: user.userId,
        })),
      };
  
      const response = await deleteUserFromList(deleteUserFromListRequest);
  
      if (response.SuccessfullyDeletedUsers?.length > 0) {
        toast.success(
          `Usuários deletados com sucesso: ${response.SuccessfullyDeletedUsers.join(", ")}`
        );
      }
  
      if (response.FailedToDeleteUsers?.length > 0) {
        toast.error(
          `Falha ao deletar os seguintes usuários: ${response.FailedToDeleteUsers.join(", ")}`
        );
      }
  
      await refreshUserList();
      refreshAdminUsers();
    } catch (error: any) {
      await refreshUserList();
      refreshAdminUsers();
    } finally {
      setSelectedRows([]);
      setIsConfirmOpen(false);
      setDeleting(false); // <-- aqui
    }
  };
  
  const handleCancelDelete = () => {
    setIsConfirmOpen(false);
    setUsersToDelete([]);
  };

  const entidadeList: string[] = Array.from(
    new Set(
      userFoldersData
        .flatMap((user) => {
          return user.permissions.map((perm) => {
            const normalizePath = (path: string) =>
              path.replace(/\\/g, "/").replace(/\/+/g, "/");
  
            const root = normalizePath(window._env_?.REACT_APP_ROOT_FOLDER || "")
              .replace(/\/+$/, "")
              .toLowerCase() + "/";
  
            const fullPath = normalizePath(perm.folderPath).toLowerCase();
  
            if (!fullPath.startsWith(root)) return null;
  
            const relative = fullPath.slice(root.length);
            const entidade = relative.split("/")[0];
            return entidade || null;
          });
        })
        .filter((x): x is string => x !== null)
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  

  return (
    <>
    {open && userFoldersData.length > 0 && (

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "580px", // Altura máxima do modal
            minHeight: "580px", // Altura mínima fixa para o modal
            minWidth: "1200px", // Largura mínima
            overflow: "hidden", // Remove scroll externo
          },
        }}
      >
        <DialogTitle>
          Gerenciamento de Usuários x Entidades
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 3,
            display: "flex",
            flexDirection: "column",
            height: "calc(100% - 64px)", // Garante que o conteúdo ocupe o espaço restante do modal
          }}
        >
          <Box //BOX DE FILTROS
            sx={{
              padding: 0,
              marginTop: 1,
              marginBottom: 0,
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center", // Centraliza os elementos verticalmente
              gap: 2,
            }}
          >
            <Box //BOX do primeiro filtro
              sx={{
                padding: 2,
                marginBottom: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "#333333" : "#e3f2fd",
                borderRadius: 1,
                boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.7)",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center", // Centraliza os elementos verticalmente
                gap: 2,
              }}
            >
              <TextField
                select
                label="Filtrar por"
                value={filter.type}
                onChange={(e) =>
                  setFilter((prev) => ({
                    ...prev,
                    type: e.target.value as "login" | "name",
                  }))
                }
                size="small"
                variant="outlined"
                sx={{
                  width: "150px", // Aumenta a largura do campo
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#444444" : "#ffffff",
                  marginBottom: 0, // Remove margem inferior
                  borderRadius: "4px", // Bordas arredondadas
                }}
                SelectProps={{
                  native: true,
                  sx: {
                    fontSize: "0.875rem", // Mantém o mesmo tamanho do texto
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: "0.875rem", // Mantém o tamanho do label igual ao outro
                  },
                }}
                InputProps={{
                  sx: {
                    height: "36px", // Ajuste para a altura do campo
                    fontSize: "0.875rem", // Texto do input no mesmo tamanho
                    padding: "0 14px", // Padding interno do input
                    border: "none", // Remove a borda interna para evitar duplicidade
                  },
                }}
              >
                <option value="login">Login</option>
                <option value="name">Nome</option>
              </TextField>

              <TextField
                placeholder={`Todos`}
                size="small"
                variant="outlined"
                value={filter.value}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, value: e.target.value }))
                }
                sx={{
                  width: "300px",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#444444" : "#ffffff",
                  marginBottom: 0, // Remove margem inferior
                  borderRadius: "4px", // Bordas arredondadas // Define a borda visível
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: "0.875rem",
                    color: (theme) => theme.palette.text.primary,
                  },
                }}
                InputProps={{
                  sx: {
                    height: "36px",
                    fontSize: "0.875rem",
                    border: "none", // Remove a borda interna para evitar duplicidade
                  },
                }}
              />
            </Box>
            <Box //BOX do segundo filtro
              sx={{
                padding: 2,
                marginBottom: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "#333333" : "#e3f2fd",
                borderRadius: 1,
                boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.6)",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center", // Centraliza os elementos verticalmente
                gap: 2,
              }}
            >
<EntityFilter
  entities={entidadeList}
  value={entidadeFilter}
  onChange={setEntidadeFilter}
/>
            </Box>
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.6)",
              borderRadius: 2,
              maxHeight: "400px", // Define a altura máxima para ativar o scroll
              overflow: "auto", // Ativa o scroll se o conteúdo exceder a altura
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    height: "32px", // Altura reduzida do cabeçalho
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#333333" : "#e3f2fd", // Garante fundo consistente
                  }}
                >
                  <TableCell
                    sx={{
                      padding: "4px 8px",
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      minWidth: "150px", // Define uma largura mínima
                    }}
                  >
                    Login
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "4px 8px",
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      minWidth: "200px", // Define uma largura mínima
                    }}
                  >
                    Nome
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "4px 8px",
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      minWidth: "300px", // Define uma largura mínima
                    }}
                  >
                    Entidades
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "4px 8px",
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      minWidth: "150px", // Define uma largura mínima
                    }}
                  >
                    Excluir Usuário
                    <Tooltip
                      title={
                        filteredRows.filter((row) => !row.isCurrentUser)
                          .length === 0
                          ? "Nenhum usuário disponível para seleção."
                          : areAllRowsSelected
                            ? "Cancelar seleção de todos os usuários."
                            : "Seleciona todos os usuários para exclusão."
                      }
                      arrow
                      placement="top"
                    >
                      <IconButton
                        aria-label="delete all users"
                        sx={{
                          cursor:
                            filteredRows.filter((row) => !row.isCurrentUser)
                              .length === 0
                              ? "not-allowed"
                              : "pointer",
                          color:
                            filteredRows.filter((row) => !row.isCurrentUser)
                              .length === 0
                              ? "gray"
                              : areAllRowsSelected
                                ? "#4CAF50"
                                : "gray",
                          "&:hover": {
                            color:
                              filteredRows.filter((row) => !row.isCurrentUser)
                                .length > 0
                                ? areAllRowsSelected
                                  ? "#4CAF50"
                                  : "red"
                                : "gray",
                          },
                        }}
                        onClick={() => {
                          if (
                            filteredRows.filter((row) => !row.isCurrentUser)
                              .length > 0
                          )
                            toggleAllRows();
                        }}
                        disabled={
                          filteredRows.filter((row) => !row.isCurrentUser)
                            .length === 0
                        }
                      >
                        {areAllRowsSelected ? (
                          <RestoreFromTrashIcon fontSize="small" />
                        ) : (
                          <DeleteForeverIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow
                    sx={{
                      height: "28px", // Altura reduzida para linhas vazias
                    }}
                  >
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{
                        fontSize: "0.875rem",
                        padding: "6px", // Menos espaço interno
                      }}
                    >
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row, index) => {
                    const isRowSelected = selectedRows.includes(index);

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          height: "28px", // Altura reduzida da linha
                          backgroundColor: isRowSelected
                            ? "rgba(244, 67, 54, 0.2)" // Fundo vermelho claro para linha selecionada
                            : "transparent", // Fundo transparente para linhas não selecionadas
                          "&:hover": {
                            backgroundColor: isRowSelected
                              ? "rgba(244, 67, 54, 0.3)" // Fundo vermelho mais intenso no hover
                              : theme.palette.mode === "dark"
                                ? "#424242"
                                : "#f1f1f1", // Fundo padrão para hover em linhas não selecionadas
                          },
                          transition: "background-color 0.3s ease-in-out", // Transição suave ao selecionar
                        }}
                      >
                        <TableCell
                          sx={{
                            padding: "2px 6px", // Reduzido para menos espaço
                            fontSize: "0.75rem",
                            lineHeight: "1",
                          }}
                        >
                          {row.login}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "2px 6px",
                            fontSize: "0.75rem",
                            lineHeight: "1",
                          }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "2px 6px",
                            fontSize: "0.75rem",
                            lineHeight: "1",
                          }}
                        >
                          {row.entidades.join(", ")}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            padding: "2px 6px",
                            fontSize: "0.75rem",
                            lineHeight: "1",
                          }}
                        >
                          <Tooltip
                            title={
                              row.isCurrentUser
                                ? "" // Removendo a mensagem do Tooltip para evitar duplicação
                                : isRowSelected
                                  ? "Cancelar seleção."
                                  : "Seleciona este usuário para exclusão."
                            }
                            arrow
                            placement="right"
                          >
                            <IconButton
                              onMouseEnter={() => {
                                if (row.isCurrentUser) {
                                  setErrorMessage(
                                    "Você não pode excluir seu próprio usuário."
                                  );
                                }
                              }}
                              onMouseLeave={() => {
                                if (row.isCurrentUser) {
                                  setErrorMessage("");
                                }
                              }}
                              onClick={() => {
                                if (!row.isCurrentUser) toggleRow(index);
                              }}
                              sx={{
                                color: row.isCurrentUser
                                  ? "gray"
                                  : isRowSelected
                                    ? "#4CAF50"
                                    : "gray",
                                cursor: row.isCurrentUser
                                  ? "not-allowed"
                                  : "pointer",
                                backgroundColor: "transparent", // Remove fundo arredondado
                                "&:hover": {
                                  color: row.isCurrentUser ? "gray" : "red",
                                  backgroundColor: "transparent", // Mantém o fundo transparente no hover
                                },
                                padding: "0", // Remove o padding para alinhar ao estilo do Admin
                                boxShadow: "none", // Remove qualquer sombra do botão
                              }}
                            >
                              {isRowSelected ? (
                                <RestoreFromTrashIcon fontSize="small" />
                              ) : (
                                <DeleteForeverIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <Divider />
        <DialogActions
          sx={{
            padding: "1px 2px",
            minHeight: "40px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              textAlign: "left",
              paddingLeft: "8px",
              fontSize: "0.875rem",
              color: "text.secondary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selectedRows.length > 0 && (
              <Typography
                sx={{
                  fontSize: "0.775rem",
                }}
              >
                {selectedRows.length ===
                filteredRows.filter((row) => !row.isCurrentUser).length
                  ? "Todos os usuários estão selecionados."
                  : selectedRows.length === 1
                    ? "1 usuário selecionado."
                    : `${selectedRows.length} usuários selecionados.`}
              </Typography>
            )}
            {selectedRows.length > 0 && errorMessage && (
              <Box
                sx={{
                  margin: "0 8px",
                  borderLeft: "1px solid",
                  borderColor: "text.secondary",
                  height: "16px",
                }}
              />
            )}
            {errorMessage && (
              <Typography
                sx={{
                  fontSize: "0.775rem",
                }}
              >
                {errorMessage}
              </Typography>
            )}
          </Box>
          <Button onClick={onClose} color="primary" size="small">
            Fechar
          </Button>
          <Button onClick={handleDownloadCSV} color="primary" size="small">
            Baixar CSV
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            size="small"
            disabled={selectedRows.length === 0} // Desabilita o botão se nenhum item estiver selecionado
            sx={{
              color: selectedRows.length === 0 ? "gray" : "primary.main", // Texto cinza quando desabilitado
              backgroundColor: "transparent", // Fundo transparente
              boxShadow: "none", // Remove qualquer sombra
              "&:hover": {
                backgroundColor:
                  selectedRows.length === 0
                    ? "transparent"
                    : "rgba(0, 0, 0, 0.04)", // Hover apenas quando habilitado
              },
              "&.Mui-disabled": {
                backgroundColor: "transparent", // Fundo transparente quando desabilitado
              },
              cursor: selectedRows.length === 0 ? "not-allowed" : "pointer", // Cursor não permitido quando desabilitado
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    )}

{/* Modal de Confirmação */}
<Dialog open={isConfirmOpen} onClose={handleCancelDelete}>
  <DialogTitle>Confirmação de Exclusão</DialogTitle>
  <DialogContent>
    {usersToDelete.length === 1 ? (
      <Typography>
        Deseja excluir o usuário <strong>{usersToDelete[0].login}</strong>?
        <br />O usuário será excluído e perderá acesso a todas as entidades.
      </Typography>
    ) : (
      <Typography>
        Deseja excluir os seguintes usuários?
        <br />
        {usersToDelete.map((user, idx) => (
          <span key={user.userId}>
            <strong>{user.login}</strong>
            {idx < usersToDelete.length - 1 ? ", " : ""}
          </span>
        ))}
        <br />
        Todos os usuários serão excluídos e perderão acesso a todas as entidades.
      </Typography>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCancelDelete} color="primary" disabled={deleting}>
      Cancelar
    </Button>
    <Button
      onClick={handleConfirmDelete}
      color="error"
      disabled={deleting}
      startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : null}
    >
      {deleting ? "Excluindo..." : "Confirmar"}
    </Button>
  </DialogActions>
</Dialog>

<Backdrop
  sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
  open={deleting}
>
  <CircularProgress color="inherit" />
</Backdrop>


      <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
      open={loading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
    </>
  );
};

export default ManagerUserModal;
